import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Bell, Check, ChevronDown, ChevronsLeft, ChevronsRight, Languages, LogOut, Monitor, Moon, Search, Settings, Sun, UserRound } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { http } from '@/lib/api/http';
import { can, useAuth } from '@/lib/auth/auth';
import { cn } from '@/lib/cn';
import { setLocale } from '@/lib/i18n';
import { LOCALES, preferences, usePreferences, type Locale, type ThemePref } from '@/lib/preferences';
import { Button } from '@/ui/button';
import { CommandPalette } from './command-palette';
import { HelpButton } from './help-panel';
import { useModuleFilter } from './use-modules';
import { AssistantWidget } from '@/features/assistant/assistant-widget';
import { MOBILE_TABS, NAV, groupLinks, isSub, type NavEntry, type NavItem, type NavSub } from './nav';
import { ForcedMfaPage, ForcedPasswordPage } from '@/features/security/security-cards';

const LOCALE_NAMES: Record<Locale, string> = { fr: 'Français', en: 'English', ar: 'العربية' };

function useUnread() {
  return useQuery({ queryKey: ['notifications', 'unread'], queryFn: () => http.get<{ unread: number }>('/notifications/unread-count'), refetchInterval: 45_000, refetchIntervalInBackground: false, select: (d) => d.unread });
}

const covers = (to: string, path: string) => (to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`));
const ALL_PATHS = NAV.flatMap((g) => groupLinks(g).map((i) => i.to));
const onPage = (items: NavItem[], path: string) => items.some((i) => (i.to === '/' ? path === '/' : path.startsWith(i.to)));
/** The open / closed state of a group or sub-menu, remembered in this browser; it opens by itself on one of its pages. */
function useFold(key: string, here: boolean, byDefault: boolean) {
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem(key); if (v !== null) return v === '1'; } catch { /* storage unavailable */ }
    return byDefault || here;
  });
  useEffect(() => { if (here && !open) setOpen(true); }, [here]);   // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = () => { const next = !open; setOpen(next); try { localStorage.setItem(key, next ? '1' : '0'); } catch { /* storage unavailable */ } };
  return [open, toggle] as const;
}
/**
 * Only the most specific menu entry is highlighted (on /sales/new: "Nouvelle facture", not also "Factures");
 * a shortcut with parameters only on its own variant ("Encaisser" and "Payer" both open /payments/new).
 */
const isActive = (item: NavItem, path: string, search: Record<string, unknown>) => {
  if (!covers(item.to, path)) return false;
  if (ALL_PATHS.some((to) => to.length > item.to.length && covers(to, path))) return false;
  return !item.search || Object.entries(item.search).every(([k, v]) => search[k] === v);
};

function NavLink({ item, collapsed, mobile, nested }: { item: NavItem; collapsed?: boolean; mobile?: boolean; nested?: boolean }) {
  const { t } = useTranslation();
  const unread = useUnread();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const active = isActive(item, path, search);
  const count = item.badge === 'inbox' ? unread.data ?? 0 : 0;
  const Icon = item.icon;
  if (mobile) {
    return (
      <Link to={item.to} search={item.search as never} aria-current={active ? 'page' : undefined} className={cn('relative flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium', active ? 'text-accent' : 'text-muted')}>
        <Icon className="size-5" aria-hidden /><span className="max-w-full truncate px-1">{t(item.label)}</span>
        {count > 0 && <span className="absolute end-[28%] top-1.5 grid min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] leading-4 text-white">{count > 99 ? '99+' : count}</span>}
      </Link>
    );
  }
  return (
    <Link
      to={item.to} search={item.search as never} aria-current={active ? 'page' : undefined} title={collapsed ? t(item.label) : undefined}
      className={cn('group relative flex items-center gap-3 rounded-md px-2.5 font-medium transition-colors', nested ? 'h-8 text-[13px]' : 'h-9 text-sm', active ? 'bg-nav-active text-white before:absolute before:inset-y-2 before:start-0 before:w-[3px] before:rounded-full before:bg-nav-indicator' : 'text-nav-fg hover:bg-nav-hover hover:text-white', collapsed && 'justify-center px-0')}
    >
      <Icon className={cn('shrink-0', nested ? 'size-4 opacity-80' : 'size-[18px]')} aria-hidden />
      {!collapsed && <span className="truncate">{t(item.label)}</span>}
      {count > 0 && !collapsed && <span className="ms-auto rounded-full bg-danger px-1.5 text-[11px] leading-5 text-white">{count > 99 ? '99+' : count}</span>}
    </Link>
  );
}

/** Groups open by default: the overview and the group of the current page. */
const OPEN_BY_DEFAULT = new Set(['nav.overview']);

/**
 * A collapsible sidebar group: the title is a button; what each person opened or closed is remembered in this browser (`navOpen:<group>`).
 * A closed group opens by itself when you land on one of its pages, and shows the unread count when it holds the inbox.
 * The narrow (icons only) sidebar has no titles and shows every icon.
 */
/** A folded set of related entries inside a group, indented under its title. */
function NavSubMenu({ sub }: { sub: NavSub }) {
  const { t } = useTranslation();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, toggle] = useFold(`navOpen:${sub.label}`, onPage(sub.items, path), false);
  const Icon = sub.icon;
  return (
    <div>
      <button type="button" onClick={toggle} aria-expanded={open}
        className="flex h-9 w-full items-center gap-3 rounded-md px-2.5 text-start text-sm font-medium text-nav-fg transition-colors hover:bg-nav-hover hover:text-white">
        <Icon className="size-[18px] shrink-0" aria-hidden />
        <span className="truncate">{t(sub.label)}</span>
        <span className="ms-auto text-[11px] text-nav-muted">{!open && sub.items.length}</span>
        <ChevronDown className={cn('size-[14px] shrink-0 text-nav-muted transition-transform duration-200', open && 'rotate-180')} aria-hidden />
      </button>
      {open && <div className="ms-[18px] mt-0.5 space-y-0.5 border-s border-nav-border ps-2">{sub.items.map((i) => <NavLink key={i.to} item={i} nested />)}</div>}
    </div>
  );
}

function NavGroup({ label, items, collapsed }: { label: string; items: NavEntry[]; collapsed: boolean }) {
  const { t } = useTranslation();
  const unread = useUnread();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const links = groupLinks({ items });
  const [open, toggle] = useFold(`navOpen:${label}`, onPage(links, path), OPEN_BY_DEFAULT.has(label));
  // The narrow sidebar (icons only) shows every link, sub-menus unfolded.
  if (collapsed) return <div className="space-y-0.5">{links.map((i) => <NavLink key={i.to + i.label} item={i} collapsed />)}</div>;
  const list = <div className="space-y-0.5">{items.map((e) => (isSub(e) ? <NavSubMenu key={e.label} sub={e} /> : <NavLink key={e.to + e.label} item={e} />))}</div>;
  const badge = !open && links.some((i) => i.badge === 'inbox') ? unread.data ?? 0 : 0;
  return (
    <div>
      <button type="button" onClick={toggle} aria-expanded={open}
        className="mb-0.5 flex w-full items-center justify-between gap-1 rounded-md px-2.5 py-1.5 text-start text-xs font-medium text-nav-muted transition-colors hover:bg-nav-hover hover:text-white">
        <span className="truncate">{t(label)}</span>
        <span className="ms-auto flex items-center gap-1">
          {badge > 0 && <span className="rounded-full bg-danger px-1.5 text-[11px] leading-5 text-white">{badge > 99 ? '99+' : badge}</span>}
          <ChevronDown className={cn('size-[15px] shrink-0 transition-transform duration-200', open && 'rotate-180')} aria-hidden />
        </span>
      </button>
      {open && list}
    </div>
  );
}

function Sidebar() {
  const { t } = useTranslation();
  const { me, company } = useAuth();
  const { sidebarCollapsed: collapsed } = usePreferences();
  const shown = useModuleFilter();
  return (
    <aside className={cn('sticky top-0 hidden h-dvh shrink-0 flex-col bg-nav text-nav-fg transition-[width] duration-200 lg:flex', collapsed ? 'w-[68px]' : 'w-64')}>
      <div className={cn('flex h-14 items-center gap-2.5 border-b border-nav-border px-4', collapsed && 'justify-center px-0')}>
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-white"><img src="/logo-mark.png" alt="" className="h-6 w-auto" aria-hidden /></span>
        {!collapsed && <div className="min-w-0"><p className="truncate text-sm font-semibold leading-tight text-white">{t('app.name')}</p><p className="truncate text-xs text-nav-muted">{company?.name}</p></div>}
      </div>
      <nav className="nav-scroll flex-1 space-y-1 overflow-y-auto p-3" aria-label="Primary">
        {NAV.map((g) => {
          const visible = (i: NavItem) => (!i.permission || can(me, i.permission)) && shown(i);
          // A sub-menu with a single visible entry is shown as that entry; an empty one disappears.
          const items = g.items.flatMap((e): NavEntry[] => {
            if (!isSub(e)) return visible(e) ? [e] : [];
            const sub = e.items.filter(visible);
            return sub.length > 1 ? [{ ...e, items: sub }] : sub;
          });
          if (!items.length) return null;
          return <NavGroup key={g.label} label={g.label} items={items} collapsed={collapsed} />;
        })}
      </nav>
      <div className="border-t border-nav-border p-3">
        <Button variant="ghost" size="sm" className={cn('w-full text-nav-fg hover:bg-nav-hover hover:text-white', collapsed ? 'px-0' : 'justify-start')} onClick={() => preferences.set({ sidebarCollapsed: !collapsed })} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {collapsed ? <ChevronsRight className="size-4 rtl:rotate-180" /> : <ChevronsLeft className="size-4 rtl:rotate-180" />}
        </Button>
      </div>
    </aside>
  );
}

function Menu({ trigger, children, label }: { trigger: ReactNode; children: ReactNode; label: string }) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild><Button variant="ghost" size="icon" aria-label={label}>{trigger}</Button></DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className="animate-in z-50 min-w-48 rounded-lg border border-border bg-surface p-1 shadow-md">{children}</DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
const Item = ({ onSelect, children, active }: { onSelect: () => void; children: ReactNode; active?: boolean }) => (
  <DropdownMenu.Item onSelect={onSelect} className="flex h-9 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm outline-none data-[highlighted]:bg-surface-2">
    {children}{active && <Check className="ms-auto size-4 text-accent" aria-hidden />}
  </DropdownMenu.Item>
);

function Topbar({ onSearch }: { onSearch: () => void }) {
  const { t } = useTranslation();
  const { me, company, logout } = useAuth();
  const { theme, locale } = usePreferences();
  const unread = useUnread();
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-surface px-4 md:px-6">
      <div className="flex min-w-0 items-center gap-2 lg:hidden">
        <img src="/logo-mark.png" alt="" className="h-8 w-auto" aria-hidden />
        <span className="truncate text-sm font-semibold">{company?.name}</span>
      </div>
      <button
        onClick={onSearch}
        className="ms-auto hidden h-9 w-full max-w-md items-center gap-2 rounded-md border border-border bg-surface-2 px-3 text-sm text-muted transition-colors hover:border-border-strong lg:ms-0 lg:flex"
        aria-label={t('nav.commandPlaceholder')}
      >
        <Search className="size-4" aria-hidden /><span className="truncate">{t('common.searchPlaceholder')}</span>
        <kbd className="ms-auto rounded border border-border bg-surface px-1.5 font-mono text-[11px]">Ctrl K</kbd>
      </button>
      <div className="ms-auto flex items-center gap-1">
        <Button variant="ghost" size="icon" className="lg:hidden" onClick={onSearch} aria-label={t('common.search')}><Search /></Button>
        <HelpButton onSearch={onSearch} />
        <Menu label={t('common.theme')} trigger={<ThemeIcon />}>
          {(['system', 'light', 'dark'] as ThemePref[]).map((v) => (
            <Item key={v} active={theme === v} onSelect={() => preferences.set({ theme: v })}>
              {v === 'system' ? <Monitor className="size-4" /> : v === 'light' ? <Sun className="size-4" /> : <Moon className="size-4" />}{t(`common.theme${v[0]!.toUpperCase()}${v.slice(1)}`)}
            </Item>
          ))}
        </Menu>
        <Menu label={t('common.language')} trigger={<Languages />}>
          {LOCALES.map((l) => <Item key={l} active={locale === l} onSelect={() => void setLocale(l)}>{LOCALE_NAMES[l]}</Item>)}
        </Menu>
        <Button variant="ghost" size="icon" className="relative" asChild aria-label={t('nav.inbox')}>
          <Link to="/inbox"><Bell />{(unread.data ?? 0) > 0 && <span className="absolute end-1.5 top-1.5 size-2 rounded-full bg-danger ring-2 ring-surface" aria-hidden />}</Link>
        </Button>
        <Menu label={t('nav.account')} trigger={<UserRound />}>
          <div className="px-2.5 py-2 text-xs text-muted"><p className="font-medium text-fg">{company?.name}</p><p>{me?.roles.join(', ')}</p></div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item asChild><Link to="/account" className="flex h-9 items-center gap-2 rounded-md px-2.5 text-sm outline-none data-[highlighted]:bg-surface-2"><Settings className="size-4" />{t('nav.preferences')}</Link></DropdownMenu.Item>
          <Item onSelect={() => void logout()}><LogOut className="size-4" />{t('auth.signOut')}</Item>
        </Menu>
      </div>
    </header>
  );
}

/** An account with a temporary password sees only the change-password screen: the server refuses everything else until it is changed. */
export function Shell() {
  const { me } = useAuth();
  if (me?.mustChangePassword) return <ForcedPasswordPage />;
  return me?.mfaEnrollmentRequired ? <ForcedMfaPage /> : <ShellInner />;
}

function ShellInner() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [palette, setPalette] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette((v) => !v); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // New route: back to the top, and the page heading receives focus for screen readers.
  useEffect(() => { window.scrollTo({ top: 0 }); document.getElementById('main')?.focus({ preventScroll: true }); }, [path]);

  return (
    <div className="flex min-h-dvh">
      <a href="#main" className="sr-only z-50 rounded-md bg-accent px-3 py-2 text-accent-fg focus:not-sr-only focus:fixed focus:start-3 focus:top-3">{t('app.skip')}</a>
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onSearch={() => setPalette(true)} />
        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-[1400px] flex-1 px-4 pb-24 pt-6 outline-none md:px-6 lg:pb-10">
          <Outlet />
        </main>
        <nav className="safe-bottom fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface/95 backdrop-blur lg:hidden" aria-label="Primary">
          {MOBILE_TABS.filter((i) => !i.permission || can(me, i.permission)).map((i) => <NavLink key={i.to} item={i} mobile />)}
        </nav>
      </div>
      <CommandPalette open={palette} onOpenChange={setPalette} />
      <AssistantWidget />
    </div>
  );
}
